import React, { useState } from 'react';
import { useField, useFormikContext } from 'formik';
import styles from "layout/Auth/Auth.module.scss";
import { AttachmentIcon, DocumentIcon, RemoveIcon } from "Assets/icons";
import { Button } from "react-bootstrap";
import { uploadFile } from "utils/common";
import { useUploadFileMutation } from "store/services/utility";
import LoadingIndicator from "../LoadingIndicator";
import { isArray, isEmpty, get } from "lodash";
import SingleUploadedFileDetail from "./SingleUploadedFileDetail";

const AppFileUploadNestedField = ({ label, acl, allowedTypes = [], onChange, handleRemove, ...props }) => {
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const { setFieldValue, values } = useFormikContext();
  const [field, meta] = useField(props);

  // Helper function to get nested value from Formik values
  const getNestedValue = (name) => {
    // Handle array syntax like clientData[6].files
    const match = name.match(/^([^\[]+)\[(\d+)\]\.(.+)$/);
    if (match) {
      const arrayName = match[1];
      const index = match[2];
      const property = match[3];
      return values[arrayName]?.[index]?.[property];
    }
    return get(values, name);
  };

  const currentValue = getNestedValue(field.name);

  const handleFileChange = async (event) => {
    try {
      const file = event.currentTarget.files?.[0];
      if (file) {
        if (allowedTypes.length && !allowedTypes.includes(file.type)) {
          alert('File type not allowed.');
          return;
        }

        const response = await uploadFile({ file, acl });
        if (!response?.error) {
          if (typeof onChange === "function") {
            onChange(field.name, response?.data);
          } else {
            // Handle nested array updates
            const match = field.name.match(/^([^\[]+)\[(\d+)\]\.(.+)$/);
            if (match) {
              const arrayName = match[1];
              const index = match[2];
              const property = match[3];
              
              // Create a copy of the array
              const newArray = [...(values[arrayName] || [])];
              // Ensure the object at the index exists
              if (!newArray[index]) newArray[index] = {};
              // Update the property
              newArray[index][property] = response?.data;
              // Update the entire array
              setFieldValue(arrayName, newArray);
            } else {
              setFieldValue(field.name, response?.data);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error uploading file:", err);
    }
  };

  const handleRemoveFile = async (index) => {
    const match = field.name.match(/^([^\[]+)\[(\d+)\]\.(.+)$/);
    
    if (match) {
      const arrayName = match[1];
      const arrayIndex = match[2];
      const property = match[3];
      
      // Create a copy of the array
      const newArray = [...values[arrayName]];
      // Remove the file at the specified index
      if (isArray(newArray[arrayIndex][property])) {
        const files = [...newArray[arrayIndex][property]];
        if (typeof handleRemove === "function") handleRemove(files[index]);
        files.splice(index, 1);
        newArray[arrayIndex][property] = files;
      } else {
        if (typeof handleRemove === "function") handleRemove(newArray[arrayIndex][property]);
        newArray[arrayIndex][property] = null;
      }
      setFieldValue(arrayName, newArray);
    } else {
      if (index || index === 0) {
        const files = [...currentValue];
        if (typeof handleRemove === "function") handleRemove(files[index]);
        files.splice(index, 1);
        setFieldValue(field.name, files);
      } else {
        if (typeof handleRemove === "function") handleRemove(currentValue);
        setFieldValue(field.name, isArray(currentValue) ? [] : null);
      }
    }
  };

  return (
    <>
      <div className="iconWithText position-relative">
        <input
          type="file"
          onChange={handleFileChange}
          className={`${styles.formControl} form-control`}
          style={{fontSize:"1rem",height:"40px"}}
          {...props}
        />
        <span
          className="icn position-absolute"
          style={{ right: 5 }}
        >
          <AttachmentIcon />
        </span>
        {isUploading && (
          <span
            className="icn position-absolute"
            style={{ right: 25 }}
          >
            <LoadingIndicator />
          </span>
        )}
      </div>
      <ul
        className={`${styles.list} list-unstyled ps-2 mb-0 pt-2`}
        style={{ maxWidth: 300 }}
      >
        {!currentValue ? (
          null
        ) : isArray(currentValue) ? (
          currentValue.map((item, index) => {
            return (
              <SingleUploadedFileDetail
                key={index}
                value={item?.attachment_name || item}
                handleRemoveFile={() => handleRemoveFile(index)}
              />
            )
          })
        ) : (
          <SingleUploadedFileDetail
            value={currentValue}
            handleRemoveFile={handleRemoveFile}
          />
        )}
      </ul>
      {meta.touched && meta.error ? (
        <div className="error">{meta.error}</div>
      ) : null}
    </>
  );
};

export default AppFileUploadNestedField;